/**
 * Abbonamenti sul telefono (7/10/2026): risponde a "quanto incassiamo ogni mese
 * a cadenza fissa, cosa si rinnova adesso e cosa non torna?".
 *
 * Prima, a 390px: quattro riquadri di numeri, le linguette, poi i rinnovi con
 * la data, il nome e l'importo uno sotto l'altro (tre righe per rinnovo), e
 * quello che non torna in fondo alla seconda colonna, dopo tre schermate.
 * Qui:
 *   1. in grande, quanto incassiamo al mese dagli abbonamenti (lo stesso numero
 *      del computer); sotto, quanto entra in più dai canoni fatturati a mano,
 *      e in rosso i pagamenti non riusciti;
 *   2. le viste: rinnovi, tutti gli abbonamenti, fuori Stripe;
 *   3. rinnovi: prima quello che c'è da sistemare (si tocca e si apre l'elenco
 *      filtrato), poi i rinnovi dei prossimi 30 giorni, poi quelli più avanti,
 *      poi quanto incassa ogni tipo di servizio;
 *   4. tutti: cerca, ordina e filtra come sul computer, una riga per
 *      abbonamento; si apre la scheda;
 *   5. fuori Stripe: i canoni fatturati a mano (si apre il progetto che pagano)
 *      e la stessa cassa contata due volte, in tre righe.
 * Attivare un abbonamento nuovo, importarli da Stripe ed esportarli restano al
 * computer: lo dice una riga in fondo.
 * Stesse chiamate e stessi parametri nell'indirizzo del computer
 * (scheda, q, ordina, stato, tipo, solo).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MANAGED_SERVICE_LABEL, SERVICE_STATUS_LABEL, type ManagedServiceType, type ServiceStatus } from '@masamune/shared';
import { api } from '../../lib/api';
import { OrdinaFiltra, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina } from '../../components/ui/OrdinaFiltra';
import { MEmpty, MGroup, MHero, MMore, MPage, MRow, MSearch, MSeg, iniziali } from '../../components/mobile/MobileKit';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur } from '../../lib/utils';
import { VisteInVista, euro, euroRiga, giornoBreve, nomeRiga } from '../admin/shared/AdminMobile';
import type { OffStripeRow, Overview, OverviewRow } from './subscriptions.types';

type Scheda = 'panoramica' | 'elenco' | 'fuori';
type Lente = 'senza-cliente' | 'scontati' | 'scaduti' | 'gratis';
const LENTI: Lente[] = ['gratis', 'scaduti', 'scontati', 'senza-cliente'];
const PAGINA = 25;

interface Abbonamento {
  id: string;
  type: ManagedServiceType;
  status: keyof typeof ServiceStatus;
  billingCycle: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  planLabel: string;
  amount: number;
  listAmount: number;
  currentPeriodEnd: string | null;
  metadata: Record<string, unknown>;
  client: { id: string; legalName: string; shortName: string } | null;
}

/** Le stesse scelte del computer (ServicesPage): ordina l'API. */
const ORDINI: OpzioneOrdina[] = [
  { key: 'stato', label: 'Prima gli attivi, in fondo i cancellati' },
  { key: 'rinnovo', label: 'Prossimo rinnovo prima' },
  { key: 'costosi', label: 'Prima i più costosi al mese' },
  { key: 'cliente', label: 'Cliente dalla A alla Z' },
];
const STATI: Array<{ key: keyof typeof ServiceStatus; label: string; conta: keyof Overview['counts'] }> = [
  { key: 'ACTIVE', label: 'Solo gli attivi', conta: 'active' },
  { key: 'PAST_DUE', label: 'Solo quelli col pagamento non riuscito', conta: 'pastDue' },
  { key: 'TRIALING', label: 'Solo quelli in prova gratuita', conta: 'trialing' },
  { key: 'PAUSED', label: 'Solo quelli in pausa', conta: 'paused' },
  { key: 'CANCELLED', label: 'Solo i cancellati', conta: 'cancelled' },
  { key: 'EXPIRED', label: 'Solo gli scaduti', conta: 'expired' },
];
const TIPI: Array<{ key: ManagedServiceType; label: string }> = [
  { key: 'HOSTING' as ManagedServiceType, label: 'Hosting Masamune' },
  { key: 'GODEAL' as ManagedServiceType, label: 'GoDeal' },
  { key: 'AURA_OS' as ManagedServiceType, label: 'Aura OS' },
  { key: 'CUSTOM' as ManagedServiceType, label: 'Servizi su misura' },
];
const LENTE_LABEL: Record<Lente, string> = {
  gratis: 'Solo gli attivi che non incassano niente',
  scaduti: 'Solo gli attivi col rinnovo già passato',
  scontati: 'Solo quelli con uno sconto permanente',
  'senza-cliente': 'Solo gli attivi senza cliente in rubrica',
};
const CICLO: Record<string, string> = { MONTHLY: 'ogni mese', QUARTERLY: 'ogni 3 mesi', YEARLY: "ogni anno", BIMONTHLY: 'ogni 2 mesi', WEEKLY: 'ogni settimana', SEMIANNUAL: 'ogni 6 mesi' };

/** Il nome da mostrare: l'anagrafica se c'è, altrimenti il nome su Stripe. */
const chi = (r: OverviewRow) => r.clientName ?? r.stripeCustomerName ?? 'Senza anagrafica';

export function AbbonamentiMobile() {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [schedaParam, setScheda] = useUrlState<Scheda>('scheda', 'panoramica');
  const { data: ov } = useQuery({ queryKey: ['services', 'overview'], queryFn: () => api.get<Overview>('/services/overview') });
  const scheda: Scheda = schedaParam === 'elenco' || (schedaParam === 'fuori' && ov?.offStripe?.items.length) ? schedaParam : 'panoramica';

  // Dalla panoramica all'elenco già filtrato, in un solo passaggio (come il computer).
  const vaiElenco = (filtro: string) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.set('scheda', 'elenco');
      for (const k of ['stato', 'tipo', 'solo', 'q']) sp.delete(k);
      sp.set(filtro === 'PAST_DUE' ? 'stato' : 'solo', filtro);
      return sp;
    });
  };

  const fuori = ov?.offStripe;
  const viste = (
    <VisteInVista chiave={scheda}>
      <MSeg<Scheda>
        value={scheda}
        onChange={setScheda}
        options={[
          { key: 'panoramica', label: 'Rinnovi' },
          { key: 'elenco', label: 'Tutti', count: ov?.counts.total },
          ...(fuori && fuori.items.length > 0 ? [{ key: 'fuori' as Scheda, label: 'Fuori Stripe', count: fuori.items.length }] : []),
        ]}
      />
    </VisteInVista>
  );

  return (
    <MPage>
      {ov && (
        <MHero
          label="Incassiamo ogni mese dagli abbonamenti"
          unit="€"
          value={fmtEur(ov.money.mrr)}
          sub={<>
            {ov.counts.vivi} attivi{fuori ? `, più ${euro(fuori.monthlyGrossNominal)} dai canoni fatturati a mano` : ''}
            {ov.attention.pastDue.length > 0 && <> · <span className="is-alert">{ov.attention.pastDue.length === 1 ? '1 pagamento non riuscito' : `${ov.attention.pastDue.length} pagamenti non riusciti`}</span></>}
          </>}
        />
      )}
      {viste}
      {scheda === 'panoramica' && (ov ? <Rinnovi ov={ov} onApri={(id) => navigate(`/services/${id}`)} onVaiElenco={vaiElenco} /> : <MGroup title="Rinnovi"><MEmpty>Carico gli abbonamenti…</MEmpty></MGroup>)}
      {scheda === 'elenco' && <Elenco ov={ov} onApri={(id) => navigate(`/services/${id}`)} />}
      {scheda === 'fuori' && ov && <FuoriStripe ov={ov} onApri={(to) => navigate(to)} />}
    </MPage>
  );
}

// ════════════════════════════════════════════════════════════════════
// Rinnovi (la panoramica)
// ════════════════════════════════════════════════════════════════════

function Rinnovi({ ov, onApri, onVaiElenco }: { ov: Overview; onApri: (id: string) => void; onVaiElenco: (f: string) => void }) {
  const [tuttiPiuAvanti, setTuttiPiuAvanti] = useState(false);
  const a = ov.attention;
  // Quello che non torna, in ordine di quanto costa (le stesse segnalazioni del computer).
  const daSistemare: Array<{ key: string; grave: boolean; titolo: string; sub: string; vai?: string }> = [];
  if (a.pastDue.length) daSistemare.push({ key: 'pastdue', grave: true, titolo: a.pastDue.length === 1 ? '1 pagamento non riuscito' : `${a.pastDue.length} pagamenti non riusciti`, sub: 'Stripe non è riuscito ad addebitare: vanno sollecitati.', vai: 'PAST_DUE' });
  if (a.zeroRevenue.length) daSistemare.push({ key: 'zero', grave: true, titolo: `${a.zeroRevenue.length} attivi che non incassano niente`, sub: `A listino sarebbero ${euro(a.zeroRevenueYearlyList)} all'anno.`, vai: 'gratis' });
  if (a.noClientActive.length) daSistemare.push({ key: 'noclient', grave: true, titolo: `${a.noClientActive.length} attivi senza cliente in rubrica`, sub: a.noClientActive.map((z) => z.stripeCustomerName ?? 'nome non disponibile').join(', '), vai: 'senza-cliente' });
  if (a.overdue.length) daSistemare.push({ key: 'overdue', grave: true, titolo: `${a.overdue.length} col rinnovo già passato`, sub: 'Il rinnovo non è registrato: Stripe non si sincronizza, o l\'addebito non è andato.', vai: 'scaduti' });
  if (a.discounted.length) daSistemare.push({ key: 'sconti', grave: false, titolo: `${a.discounted.length} scontati per sempre`, sub: `Coupon senza scadenza: ${euro(a.discountYearly)} all'anno di differenza.`, vai: 'scontati' });
  // I chiusi senza cliente (sul computer in fondo a "Quello che non torna") qui non ci sono:
  // non incassano più, non c'è niente da sistemare. Si trovano in "Tutti", filtrando i cancellati.

  const riga = (r: OverviewRow) => (
    <MRow
      key={r.id}
      text={iniziali(chi(r))}
      round
      title={nomeRiga(chi(r))}
      sub={`${giornoBreve(r.currentPeriodEnd)} · ${r.planLabel}`}
      trail={r.amount === 0 ? <span className="is-alert">gratis</span> : euroRiga(r.amount)}
      onClick={() => onApri(r.id)}
    />
  );
  const piuAvanti = ov.renewals.later.items;

  return (
    <>
      {daSistemare.length > 0 && (
        <MGroup title="Da sistemare" count={daSistemare.length}>
          {daSistemare.map((s) => (
            <MRow key={s.key} icon={s.grave ? 'alert' : 'help'} tone={s.grave ? 'alert' : undefined} title={s.titolo} sub={s.sub} subWrap
              onClick={s.vai ? () => onVaiElenco(s.vai!) : undefined} />
          ))}
        </MGroup>
      )}

      <MGroup title={`Si rinnovano entro ${ov.renewals.windowDays} giorni`} count={ov.renewals.next30.count || undefined}
        note={ov.renewals.next30.count ? `${euro(ov.renewals.next30.amount)} in arrivo.` : undefined}>
        {ov.renewals.next30.items.length === 0
          ? <MEmpty>Niente in scadenza nei prossimi {ov.renewals.windowDays} giorni.</MEmpty>
          : ov.renewals.next30.items.map(riga)}
      </MGroup>

      {piuAvanti.length > 0 && (
        <MGroup title={`Più avanti, entro ${ov.renewals.horizonDays} giorni`} count={ov.renewals.later.count}>
          {(tuttiPiuAvanti ? piuAvanti : piuAvanti.slice(0, 3)).map(riga)}
          {piuAvanti.length > 3 && <MMore label={tuttiPiuAvanti ? 'Mostra meno' : `Vedi tutti i ${piuAvanti.length}`} onClick={() => setTuttiPiuAvanti((v) => !v)} />}
        </MGroup>
      )}

      <MGroup title="Cosa incassiamo, per tipo" note="I nuovi abbonamenti si attivano dal computer, come l'importazione da Stripe.">
        {ov.byType.map((t) => (
          <MRow key={t.type} title={MANAGED_SERVICE_LABEL[t.type]} sub={`${t.active} attivi${t.total > t.active ? `, ${t.total - t.active} chiusi nel tempo` : ''}`}
            trail={euro(t.mrr)} trailSub="al mese" />
        ))}
      </MGroup>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Tutti gli abbonamenti
// ════════════════════════════════════════════════════════════════════

function Elenco({ ov, onApri }: { ov: Overview | undefined; onApri: (id: string) => void }) {
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const ordina = useOrdina(ORDINI);
  const defs = useMemo<FiltroDef[]>(() => [
    { param: 'stato', label: "Stato dell'abbonamento", opzioni: STATI.map((s) => ({ value: s.key, label: s.label, count: ov?.counts[s.conta] })) },
    { param: 'tipo', label: 'Tipo di servizio', opzioni: TIPI.map((t) => ({ value: t.key, label: t.label, count: ov ? (ov.byType.find((b) => b.type === t.key)?.total ?? 0) : undefined })) },
    {
      param: 'solo', label: 'Da controllare',
      opzioni: LENTI.map((l) => ({
        value: l, label: LENTE_LABEL[l],
        count: !ov ? undefined : l === 'gratis' ? ov.attention.zeroRevenue.length : l === 'scaduti' ? ov.attention.overdue.length : l === 'scontati' ? ov.attention.discounted.length : ov.attention.noClientActive.length,
      })),
    },
  ], [ov]);
  const filtri = useFiltri(defs);
  const tipo = filtri.valore('tipo');
  const stato = filtri.valore('stato');
  const lente = (LENTI as string[]).includes(filtri.valore('solo')) ? (filtri.valore('solo') as Lente) : '';
  const [quante, setQuante] = useState(PAGINA);
  useEffect(() => { setQuante(PAGINA); }, [filtri.chiave, search, ordina.key]);

  // Le "lenti" non esistono sull'API: sono insiemi della panoramica (come sul computer).
  const idsLente = useMemo(() => {
    if (!ov || !lente) return null;
    const a = ov.attention;
    const src = lente === 'senza-cliente' ? a.noClientActive : lente === 'scontati' ? a.discounted : lente === 'scaduti' ? a.overdue : a.zeroRevenue;
    return new Set(src.map((r) => r.id));
  }, [ov, lente]);

  const params = new URLSearchParams();
  if (tipo) params.set('type', tipo);
  if (stato) params.set('status', stato);
  if (search) params.set('search', search);
  params.set('sort', ordina.key);
  params.set('page', '1');
  params.set('pageSize', String(idsLente ? 200 : quante));
  const { data, isLoading } = useQuery({
    queryKey: ['services', 'telefono', tipo, stato, search, ordina.key, quante, !!idsLente],
    queryFn: () => api.get<{ items: Abbonamento[]; total: number }>(`/services?${params.toString()}`),
    placeholderData: keepPreviousData,
  });
  const righe = (data?.items ?? []).filter((s) => !idsLente || idsLente.has(s.id));
  const total = idsLente ? righe.length : data?.total ?? 0;

  return (
    <>
      <div className="amk-tools">
        <MSearch value={search} onChange={setSearch} placeholder="Cliente, piano, id Stripe" />
        <OrdinaFiltra ordina={ordina} filtri={filtri} label="Filtra" />
      </div>

      {lente && (
        <div className="mk-list">
          <MRow icon="filter" title={LENTE_LABEL[lente]} sub="Tocca per vederli tutti" onClick={() => filtri.set('solo', [])} chevron={false} />
        </div>
      )}

      <MGroup title={ordina.attiva.label} count={data ? total : undefined}>
        {isLoading && !data ? <MEmpty>Carico gli abbonamenti…</MEmpty>
          : righe.length === 0 ? <MEmpty>{filtri.attivi || search.trim() ? 'Nessun abbonamento con questa ricerca o questi filtri.' : 'Nessun abbonamento ancora.'}</MEmpty>
          : (
            <>
              {righe.map((s) => {
                const nome = s.client?.legalName ?? (s.metadata?.stripeCustomerName as string | undefined) ?? 'Senza anagrafica';
                const attivo = s.status === 'ACTIVE';
                return (
                  <MRow
                    key={s.id}
                    text={iniziali(nome)}
                    round
                    title={nomeRiga(nome)}
                    // Prima la data o lo stato (cambiano da riga a riga), poi il piano (si ripete uguale).
                    sub={attivo
                      ? `${s.currentPeriodEnd ? `rinnovo ${giornoBreve(s.currentPeriodEnd)} · ` : ''}${s.planLabel}`
                      : <>{s.status === 'PAST_DUE' ? <span className="is-alert">pagamento non riuscito</span> : SERVICE_STATUS_LABEL[s.status].toLowerCase()} · {s.planLabel}</>}
                    trail={s.amount === 0 ? <span className={attivo ? 'is-alert' : 'amk-manca'}>gratis</span> : euroRiga(s.amount)}
                    trailSub={CICLO[s.billingCycle]}
                    onClick={() => onApri(s.id)}
                  />
                );
              })}
              {!idsLente && total > righe.length && <MMore label={`Vedi altri ${Math.min(PAGINA, total - righe.length)}`} onClick={() => setQuante((n) => n + PAGINA)} />}
            </>
          )}
      </MGroup>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Fuori Stripe
// ════════════════════════════════════════════════════════════════════

/** Quando un canone non si incassa adesso: lo si dice, se no sembrano due canoni sommabili. */
function nota(r: OffStripeRow): string | null {
  if (r.phase === 'futuro') return `comincia il ${giornoBreve(r.startDate)}, fuori dal totale`;
  if (r.phase === 'concluso') return `finito il ${giornoBreve(r.endDate)}`;
  if (r.endDate) return `ultimo addebito il ${giornoBreve(r.endDate)}`;
  return null;
}

function FuoriStripe({ ov, onApri }: { ov: Overview; onApri: (to: string) => void }) {
  const off = ov.offStripe;
  if (!off) return <MGroup title="Fuori Stripe"><MEmpty>I canoni fuori Stripe stanno in Amministrazione: per vederli serve il permesso su quella sezione.</MEmpty></MGroup>;
  const rec = ov.reconciliation;
  const righe = [...off.items].sort((a, b) => a.nextDueDate.localeCompare(b.nextDueDate));
  return (
    <>
      <MGroup title="Ogni mese, a cadenza fissa"
        note={`IVA inclusa, per confrontarli con l'addebito Stripe.${off.futureCount > 0 ? ` Fuori dal totale ${off.futureCount === 1 ? 'un canone che comincia' : `${off.futureCount} canoni che cominciano`} più avanti: ${euro(off.monthlyGrossFuture)} al mese.` : ''}`}>
        <MRow icon="repeat" title="Dagli abbonamenti Stripe" trail={euro(ov.money.mrr)} />
        <MRow icon="invoice" title="Dai canoni fatturati a mano" trail={euro(off.monthlyGrossNominal)} />
        <MRow icon="coins" title="In tutto" trail={euro(ov.money.mrr + off.monthlyGrossNominal)} />
      </MGroup>

      <MGroup title="Canoni fatturati a mano" count={righe.length}
        note="L'importo è la rata, non il valore del contratto: quello sta sul progetto, e le due cifre non si sommano.">
        {righe.map((r) => {
          const n = nota(r);
          return (
            <MRow
              key={r.id}
              text={iniziali(r.clientName ?? r.name)}
              round
              title={nomeRiga(r.clientName ?? r.name)}
              sub={<>{n ?? `prossima il ${giornoBreve(r.nextDueDate)}`}{r.project ? ` · ${r.project.name}` : ''}</>}
              trail={<span className={r.phase === 'in_corso' ? undefined : 'amk-manca'}>{euroRiga(r.amountGross)}</span>}
              trailSub={CICLO[r.cycle] ?? r.cycle}
              onClick={r.project ? () => onApri(`/projects/${r.project!.id}`) : r.clientId ? () => onApri(`/contatti/client/${r.clientId}`) : undefined}
            />
          );
        })}
      </MGroup>

      {rec && rec.aggregateItems.length > 0 && (
        <MGroup title="La stessa cassa contata due volte"
          note="Le voci stimate stavano in Amministrazione prima che Stripe entrasse qui: finché restano accese la previsione conta due volte gli stessi soldi. Spegnerle è una scelta di Lorenzo, dal computer.">
          <MRow title="Le voci stimate promettono" sub={plurale(rec.aggregateItems.length)} trail={euro(rec.aggregateMonthlyNet)} trailSub="al mese" />
          <MRow title="Stripe incassa davvero" trail={euro(rec.stripeMonthlyGross)} trailSub="al mese" />
          <MRow title="Differenza" trail={<span className="is-alert">{euro(rec.aggregateMonthlyNet - rec.stripeMonthlyGross)}</span>} trailSub="al mese" />
          <MRow title="Fatture degli abbonamenti già qui" sub={`emesse da sole da ${new Date(rec.since).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}`}
            trail={euro(rec.invoicedGross)} trailSub={`${rec.invoicedCount} fatture`} />
        </MGroup>
      )}
    </>
  );
}

const plurale = (n: number): ReactNode => (n === 1 ? '1 voce' : `${n} voci`);
